import { useEffect, useMemo, useState } from 'react';

import { BulkActionBar } from 'pages/contacts/BulkActionBar';

import { useAppState } from '../../AppStateProvider';
import { CompanyName } from '../../components/CompanyName';
import { EmptyStateView } from '../../components/EmptyStateView';
import { GainAccessView } from '../../components/GainAccessView';
import { SearchTextBox } from '../../components/SearchTextBox';
import { useCompanyQuery } from '../../features/company/useCompanyQuery';
import { useContactListQuery } from '../../features/contacts/companyContactList/useContactListQuery';
import { useContactsCountByCountriesQuery } from '../../features/contacts/companyContactList/useContactsCountByCountriesQuery';
import { useContactsAvailabilityStatus } from '../../features/contacts/hooks/useContactsAvailabilityStatus';
import { appStore } from '../../services/appStore';
import { translate } from '../../services/i18n';
import { useDispatchTrackEventWithLocation } from '../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { StyledContainerWithNav, StyledContactsContainerWithNav } from '../styles';

import { ContactFilters } from './ContactFilters';
import { ContactListInfinite } from './ContactList';
import { PersonaFilter } from './PersonaFilter';
import {
  StyledCompanyName,
  StyledContactsScrollContainer,
  StyledContactsHeader,
  StyledContactsFilters,
  StyledContactsHeaderContainer,
} from './styles';

export const Contacts = () => {
  const { contactFilters } = useAppState();
  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();
  const { isContactsDisabled } = useContactsAvailabilityStatus();
  const [shouldAnimatePrivateDataMount, setShouldAnimatePrivateDataMount] = useState(false);

  const companyQuery = useCompanyQuery();
  const company = companyQuery.data;

  const contactListQueryResult = useContactListQuery();
  const { isLoading: isLoadingFilters } = useContactsCountByCountriesQuery();

  const contacts = useMemo(() => contactListQueryResult.data?.pages.flat() || [], [contactListQueryResult.data]);

  useEffect(() => {
    appStore.setSelectedContacts([]);
  }, [contactFilters]);

  const handleTrackSearch = (searchText: string) => {
    appStore.setContactsSearchText(searchText);
    dispatchTrackEventWithLocation('contacts options', 'apply enter', `apply search/contact name search/${searchText}`);
  };

  if (isContactsDisabled) {
    return <GainAccessView variant="contacts-disabled" />;
  }

  if (!company || companyQuery.isError) {
    return (
      <StyledContainerWithNav data-automation="EmptyContacts">
        <EmptyStateView
          messageKey="company.empty_state.message_linkedin"
          img="/images/empty-state-lens.svg"
          variant="lens"
        />
      </StyledContainerWithNav>
    );
  }

  return (
    <StyledContactsContainerWithNav>
      <StyledContactsScrollContainer>
        <StyledContactsHeaderContainer>
          <StyledContactsHeader>
            <StyledCompanyName data-automation="EmployeeCompanyName">
              <CompanyName company={company} variant="contacts_list" />
            </StyledCompanyName>
            <SearchTextBox
              searchText={contactFilters.searchText}
              onChange={handleTrackSearch}
              placeholder={translate('contacts.search_placeholder')}
              isLoading={contactListQueryResult.isLoading}
            />
            <StyledContactsFilters>
              <PersonaFilter isDisabled={isLoadingFilters} />
              <ContactFilters contactsNumber={contacts.length} />
            </StyledContactsFilters>
          </StyledContactsHeader>
          <BulkActionBar contacts={contacts} setShouldAnimatePrivateDataMount={setShouldAnimatePrivateDataMount} />
        </StyledContactsHeaderContainer>
        <ContactListInfinite
          {...contactListQueryResult}
          shouldAnimatePrivateDataMount={shouldAnimatePrivateDataMount}
        />
      </StyledContactsScrollContainer>
    </StyledContactsContainerWithNav>
  );
};
